import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { UI, btnSx } from "../components/ActionDrawer";
import DashTable, {
  FilterChips,
  PageHead,
  SearchBox,
  Tag,
} from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";
import { getPermissions } from "../store/rootReducer";

const PAGE_SIZE = 15;
const REVEAL_VISIBLE_SECONDS = 30;
const WAITING_WARN_DAYS = 3;
const WAITING_ALARM_DAYS = 7;

const STATUS_META = {
  NOT_STARTED: { label: "Not started", tone: "i" },
  SUBMITTED: { label: "Submitted", tone: "s" },
  UNDER_REVIEW: { label: "Under review", tone: "bl" },
  NEEDS_CLARIFICATION: { label: "Needs clarification", tone: "s" },
  ACTIVATED: { label: "Activated", tone: "g" },
  REJECTED: { label: "Rejected", tone: "b" },
  SUSPENDED: { label: "Suspended", tone: "b" },
};

const ALL_STATUSES = Object.keys(STATUS_META).join(",");
const QUEUE_STATUSES = ["SUBMITTED", "UNDER_REVIEW"];

/* What a reviewer may do from each status (payout-review-api.md, 3.2). */
const ACTIONS_BY_STATUS = {
  SUBMITTED: ["under_review", "clarify", "reject", "activate"],
  UNDER_REVIEW: ["clarify", "reject", "activate"],
  ACTIVATED: ["suspend"],
  SUSPENDED: ["reactivate"],
};

/* kind: primary = the move that goes forward, danger = stops money or closes
   the case, ghost = everything else. */
const ACTION_META = {
  under_review: { label: "Start review", kind: "ghost", done: "Marked as under review" },
  clarify: {
    label: "Ask clinic to fix",
    kind: "ghost",
    needsMessage: true,
    done: "Clarification sent to the clinic",
  },
  reject: {
    label: "Reject",
    kind: "danger",
    needsMessage: true,
    done: "Payout account rejected",
  },
  activate: {
    label: "Activate",
    kind: "primary",
    needsAccountId: true,
    done: "Payout account activated",
  },
  suspend: {
    label: "Suspend",
    kind: "danger",
    needsMessage: true,
    done: "Payout account suspended",
  },
  reactivate: {
    label: "Reactivate",
    kind: "primary",
    optionalAccountId: true,
    done: "Payout account reactivated",
  },
};

const dangerSx = (filled) => ({
  ...btnSx(filled ? "primary" : "ghost"),
  ...(filled
    ? { bgcolor: UI.red, "&:hover": { bgcolor: UI.red, opacity: 0.9 } }
    : { color: UI.red, borderColor: "rgba(198, 40, 40, 0.35)", "&:hover": { bgcolor: UI.redBg } }),
});

const actionSx = (kind, selected) => {
  if (kind === "danger") return dangerSx(selected);
  if (kind === "primary") return btnSx("primary");
  return btnSx(selected ? "primary" : "ghost");
};

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const dateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const daysSince = (iso) => {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return null;
  return Math.max(Math.floor(diff / (1000 * 60 * 60 * 24)), 0);
};

const StatusTag = ({ status }) => {
  const meta = STATUS_META[status] || { label: status || "—", tone: "i" };
  return <Tag tone={meta.tone}>{meta.label}</Tag>;
};

/* How long a queue item has been waiting, coloured once it is getting old. */
const WaitingTag = ({ row }) => {
  if (!QUEUE_STATUSES.includes(row.activationStatus)) return "—";
  const days = daysSince(row.submittedAt);
  if (days === null) return "—";
  const tone = days >= WAITING_ALARM_DAYS ? "b" : days >= WAITING_WARN_DAYS ? "s" : "i";
  return <Tag tone={tone}>{days === 0 ? "Today" : `${days}d`}</Tag>;
};

const errorText = (error, fallback) =>
  error?.response?.data?.message ||
  (error?.response?.status === 429 ? "Too many requests, try again in a minute." : fallback);

const auditMeta = (entry) => {
  if (entry.action === "SUBMITTED") return { text: "Submitted by the clinic", color: UI.blue };
  if (entry.action === "VIEWED_DETAILS") return { text: "Account number revealed", color: UI.ink4 };
  const from = STATUS_META[entry.fromStatus]?.label || entry.fromStatus;
  const to = STATUS_META[entry.toStatus]?.label || entry.toStatus;
  const color =
    entry.toStatus === "ACTIVATED"
      ? UI.green
      : ["REJECTED", "SUSPENDED"].includes(entry.toStatus)
        ? UI.red
        : UI.amber;
  return { text: `${from} to ${to}`, color };
};

const textareaSx = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid",
  borderColor: UI.lineIn,
  borderRadius: "4px",
  padding: "9px 11px",
  bgcolor: UI.surface,
  color: UI.ink,
  fontSize: 13,
  fontFamily: "inherit",
  outline: "none",
  resize: "vertical",
  minHeight: 64,
  "&:focus": { borderColor: UI.p },
};

/* ---- small building blocks for the detail panel ---- */

const Section = ({ title, children }) => (
  <Box sx={{ marginBottom: "22px" }}>
    <Box
      sx={{
        fontSize: 11,
        letterSpacing: ".6px",
        textTransform: "uppercase",
        color: UI.ink3,
        fontWeight: 600,
        marginBottom: "4px",
      }}
    >
      {title}
    </Box>
    {children}
  </Box>
);

const Row = ({ label, children, last }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "12px",
      padding: "9px 0",
      borderBottom: last ? 0 : "1px solid",
      borderBottomColor: UI.line,
      fontSize: 13,
    }}
  >
    <Box component="span" sx={{ color: UI.ink3, flexShrink: 0 }}>
      {label}
    </Box>
    <Box
      component="span"
      sx={{
        color: UI.ink,
        fontWeight: 500,
        textAlign: "right",
        minWidth: 0,
        overflowWrap: "anywhere",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "8px",
        flexWrap: "wrap",
      }}
    >
      {children}
    </Box>
  </Box>
);

const CopyButton = ({ value, label }) => {
  const toast = useToast();
  if (!value) return null;
  return (
    <Box
      component="button"
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          toast.success(`${label} copied`);
        } catch (error) {
          toast.error("Could not copy");
        }
      }}
      sx={{ ...btnSx("ghost"), padding: "2px 8px", fontSize: 11 }}
    >
      Copy
    </Box>
  );
};

/* Review panel for one clinic. The account number is only ever held in this
   component's state, hidden again after REVEAL_VISIBLE_SECONDS or when the
   panel closes, and is never logged or copied. */
const PayoutDetail = ({ clinicId, canAct, token, onClose, onChanged }) => {
  const toast = useToast();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [revealed, setRevealed] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [revealing, setRevealing] = useState(false);
  const tick = useRef(null);

  const [action, setAction] = useState(null);
  const [message, setMessage] = useState("");
  const [accountId, setAccountId] = useState("");
  const [verifyNote, setVerifyNote] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const hideNumber = useCallback(() => {
    if (tick.current) clearInterval(tick.current);
    tick.current = null;
    setRevealed("");
    setSecondsLeft(0);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      setDetail(await apiService.fetchPayoutAccount(clinicId, token));
    } catch (error) {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [clinicId, token]);

  useEffect(() => {
    load();
    return () => hideNumber();
  }, [load, hideNumber]);

  const resetAction = () => {
    setAction(null);
    setMessage("");
    setAccountId("");
    setVerifyNote(null);
  };

  const handleReveal = async () => {
    setRevealing(true);
    try {
      const res = await apiService.revealPayoutAccountNumber(clinicId, token);
      setRevealed(res.accountNumber || "");
      setSecondsLeft(REVEAL_VISIBLE_SECONDS);
      if (tick.current) clearInterval(tick.current);
      tick.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) {
            hideNumber();
            return 0;
          }
          return s - 1;
        });
      }, 1000);
      // The reveal is written to the audit log, so show it without a reload.
      load();
    } catch (error) {
      toast.error(errorText(error, "Could not reveal the account number"));
    } finally {
      setRevealing(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyNote(null);
    try {
      const res = await apiService.verifyPayoutAccountId(
        { razorpayAccountId: accountId.trim(), clinicId },
        token,
      );
      setVerifyNote({
        ok: !res.warning,
        text:
          res.warning ||
          `Found on Razorpay (status: ${res.razorpayStatus}). Also confirm in the Razorpay Dashboard that it can receive settlements.`,
      });
    } catch (error) {
      setVerifyNote({ ok: false, text: errorText(error, "Could not verify the id") });
    } finally {
      setVerifying(false);
    }
  };

  const meta = action ? ACTION_META[action] : null;
  const canSubmit =
    meta && (!meta.needsMessage || message.trim()) && (!meta.needsAccountId || accountId.trim());

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = { action };
      if (meta.needsMessage) payload.message = message.trim();
      if ((meta.needsAccountId || meta.optionalAccountId) && accountId.trim()) {
        payload.razorpayAccountId = accountId.trim();
      }
      await apiService.transitionPayoutAccount(clinicId, payload, token);
      toast.success(meta.done);
      hideNumber();
      resetAction();
      await load();
      onChanged();
    } catch (error) {
      toast.error(errorText(error, "Could not change the status"));
      // Someone else may have acted first, so show the current state.
      if (error?.response?.status === 409) load();
    } finally {
      setSubmitting(false);
    }
  };

  const availableActions = detail ? ACTIONS_BY_STATUS[detail.activationStatus] || [] : [];
  const showFooter = canAct && availableActions.length > 0;
  const location = [detail?.clinic?.city, detail?.clinic?.state].filter(Boolean).join(", ");

  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          paddingBottom: "12px",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: 18, lineHeight: 1.3 }}>
            {detail?.clinic?.name || "Payout account"}
          </Box>
          {location ? (
            <Box sx={{ fontSize: 12, color: UI.ink3, fontWeight: 400, marginTop: "2px" }}>
              {location}
            </Box>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          {detail ? <StatusTag status={detail.activationStatus} /> : null}
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && !detail && <PageLoader />}
        {loadFailed && (
          <Box sx={{ color: UI.red, fontSize: 13 }}>
            Could not load this payout account.{" "}
            <Box component="button" type="button" onClick={load} sx={btnSx("ghost")}>
              Retry
            </Box>
          </Box>
        )}

        {detail && (
          <>
            {detail.statusMessage ? (
              <Box
                sx={{
                  bgcolor: UI.amberBg,
                  border: "1px solid",
                  borderColor: UI.amberLine,
                  borderRadius: "4px",
                  padding: "10px 12px",
                  fontSize: 12,
                  color: UI.ink,
                  marginBottom: "18px",
                }}
              >
                <Box sx={{ color: UI.ink3, marginBottom: "2px" }}>Message shown to the clinic</Box>
                {detail.statusMessage}
              </Box>
            ) : null}

            <Section title="Business">
              <Row label="Legal business name">{detail.legalBusinessName || "—"}</Row>
              <Row label="Business type">{detail.businessType || "—"}</Row>
              <Row label="Email">
                {detail.email || "—"}
                <CopyButton value={detail.email} label="Email" />
              </Row>
              <Row label="Submitted" last>
                {dateTime(detail.submittedAt)}
                {detail.submittedByName ? ` by ${detail.submittedByName}` : ""}
              </Row>
            </Section>

            <Section title="Bank account">
              <Row label="Beneficiary name">{detail.beneficiaryName || "—"}</Row>
              <Row label="IFSC">
                <Box component="span" sx={{ fontFamily: UI.mono }}>
                  {detail.ifsc || "—"}
                </Box>
                <CopyButton value={detail.ifsc} label="IFSC" />
              </Row>
              <Row label="Account number">
                {revealed ? (
                  <>
                    <Box
                      component="span"
                      sx={{ fontFamily: UI.mono, fontSize: 15, letterSpacing: ".5px" }}
                    >
                      {revealed}
                    </Box>
                    <Box component="span" sx={{ fontSize: 11, color: UI.ink3, fontWeight: 400 }}>
                      hides in {secondsLeft}s
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={hideNumber}
                      sx={{ ...btnSx("ghost"), padding: "2px 8px", fontSize: 11 }}
                    >
                      Hide
                    </Box>
                  </>
                ) : detail.hasAccountNumber ? (
                  <>
                    <Box component="span" sx={{ fontFamily: UI.mono, color: UI.ink3, letterSpacing: "2px" }}>
                      ••••••••••
                    </Box>
                    {canAct ? (
                      <Box
                        component="button"
                        type="button"
                        onClick={handleReveal}
                        disabled={revealing}
                        sx={{ ...btnSx("ghost"), padding: "2px 10px", fontSize: 12 }}
                      >
                        {revealing ? "Revealing…" : "Reveal"}
                      </Box>
                    ) : null}
                  </>
                ) : (
                  "Not on record"
                )}
              </Row>
              <Row label="Platform fee" last>
                {detail.platformFeePercentage != null ? `${detail.platformFeePercentage}%` : "—"}
              </Row>
              {canAct && detail.hasAccountNumber && !revealed ? (
                <Box sx={{ fontSize: 11, color: UI.ink3, marginTop: "6px" }}>
                  Each reveal is written to the audit log.
                </Box>
              ) : null}
            </Section>

            <Section title="Razorpay">
              <Row label="Linked account id">
                <Box component="span" sx={{ fontFamily: UI.mono }}>
                  {detail.razorpayAccountId || "—"}
                </Box>
                <CopyButton value={detail.razorpayAccountId} label="Account id" />
              </Row>
              <Row label="Terms accepted">
                {detail.tncAccepted ? dateTime(detail.tncAcceptedAt) : "No"}
              </Row>
              <Row label="Last reviewed" last>
                {detail.reviewedAt
                  ? `${dateTime(detail.reviewedAt)}${detail.reviewedByName ? ` by ${detail.reviewedByName}` : ""}`
                  : "—"}
              </Row>
            </Section>

            <Section title="History">
              {(detail.auditLog || []).length === 0 ? (
                <Box sx={{ fontSize: 12, color: UI.ink3, padding: "6px 0" }}>No history yet.</Box>
              ) : (
                <Box sx={{ position: "relative", paddingLeft: "18px", marginTop: "6px" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      left: "4px",
                      top: "6px",
                      bottom: "6px",
                      width: "1px",
                      bgcolor: UI.line,
                    }}
                  />
                  {detail.auditLog
                    .slice()
                    .reverse()
                    .map((entry, index) => {
                      const info = auditMeta(entry);
                      return (
                        <Box
                          key={`${entry.at}-${index}`}
                          sx={{ position: "relative", paddingBottom: "14px", fontSize: 12 }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              left: "-18px",
                              top: "4px",
                              width: "9px",
                              height: "9px",
                              borderRadius: "50%",
                              bgcolor: info.color,
                            }}
                          />
                          <Box sx={{ color: UI.ink, fontWeight: 500 }}>{info.text}</Box>
                          <Box sx={{ color: UI.ink3 }}>
                            {dateTime(entry.at)}
                            {entry.byName ? ` · ${entry.byName}` : ""}
                          </Box>
                          {entry.action !== "VIEWED_DETAILS" && entry.note ? (
                            <Box sx={{ color: UI.ink3, fontStyle: "italic" }}>"{entry.note}"</Box>
                          ) : null}
                        </Box>
                      );
                    })}
                </Box>
              )}
            </Section>
          </>
        )}
      </DialogContent>

      {showFooter && (
        <DialogActions
          sx={{ display: "block", padding: "14px 24px", bgcolor: UI.surface }}
        >
          <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            <Box component="span" sx={{ fontSize: 12, color: UI.ink3, marginRight: "4px" }}>
              Review
            </Box>
            {availableActions.map((key) => (
              <Box
                key={key}
                component="button"
                type="button"
                onClick={() => {
                  resetAction();
                  setAction(key);
                }}
                sx={actionSx(ACTION_META[key].kind, action === key)}
              >
                {ACTION_META[key].label}
              </Box>
            ))}
          </Box>

          {meta && (
            <Box sx={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
              {meta.needsMessage && (
                <Box>
                  <Box sx={{ fontSize: 12, color: UI.ink3, marginBottom: "4px" }}>
                    Message for the clinic (shown to them as written)
                  </Box>
                  <Box
                    component="textarea"
                    value={message}
                    maxLength={500}
                    onChange={(e) => setMessage(e.target.value)}
                    sx={textareaSx}
                  />
                  <Box sx={{ fontSize: 11, color: UI.ink4, textAlign: "right" }}>
                    {message.length}/500
                  </Box>
                </Box>
              )}
              {(meta.needsAccountId || meta.optionalAccountId) && (
                <Box>
                  <Box sx={{ fontSize: 12, color: UI.ink3, marginBottom: "4px" }}>
                    {meta.optionalAccountId
                      ? "Razorpay account id (leave empty to reuse the stored one)"
                      : "Razorpay account id (create the linked account in the Razorpay Dashboard first)"}
                  </Box>
                  <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <SearchBox
                      value={accountId}
                      onChange={(e) => {
                        setAccountId(e.target.value);
                        setVerifyNote(null);
                      }}
                      placeholder="acc_XXXXXXXXXXXXXX"
                      width="100%"
                    />
                    <Box
                      component="button"
                      type="button"
                      onClick={handleVerify}
                      disabled={!accountId.trim() || verifying}
                      sx={btnSx("ghost", !accountId.trim() || verifying)}
                    >
                      {verifying ? "Checking…" : "Verify"}
                    </Box>
                  </Box>
                  {verifyNote && (
                    <Box
                      sx={{
                        fontSize: 12,
                        marginTop: "6px",
                        color: verifyNote.ok ? UI.green : UI.red,
                      }}
                    >
                      {verifyNote.text}
                    </Box>
                  )}
                </Box>
              )}
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}
                  sx={
                    meta.kind === "danger" && canSubmit && !submitting
                      ? dangerSx(true)
                      : btnSx("primary", !canSubmit || submitting)
                  }
                >
                  {submitting ? "Saving…" : `Confirm: ${meta.label}`}
                </Box>
                <Box component="button" type="button" onClick={resetAction} sx={btnSx("flat")}>
                  Cancel
                </Box>
              </Box>
            </Box>
          )}
        </DialogActions>
      )}
    </>
  );
};

const EMPTY_TEXT = {
  queue: "Nothing waiting for review. All caught up.",
  NEEDS_CLARIFICATION: "No clinics are waiting on a fix from their side.",
  ACTIVATED: "No activated payout accounts yet.",
  REJECTED: "No rejected payout accounts.",
  SUSPENDED: "No suspended payout accounts.",
  all: "No payout accounts yet.",
};

const PayoutReview = () => {
  const toast = useToast();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");
  const permissions = useSelector(getPermissions) || {};
  const canAct = Boolean(permissions.payoutReviewActions);

  const [filter, setFilter] = useState("queue");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ results: [], total: 0, limit: PAGE_SIZE });
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const statusParam =
    filter === "queue" ? QUEUE_STATUSES.join(",") : filter === "all" ? ALL_STATUSES : filter;

  const fetchRows = useCallback(async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const [list, counts] = await Promise.all([
        apiService.fetchPayoutAccounts(token, {
          status: statusParam,
          search: debouncedSearch || undefined,
          page,
          limit: PAGE_SIZE,
        }),
        apiService.fetchPayoutSummary(token),
      ]);
      setData(list);
      setSummary(counts);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching payout accounts");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusParam, debouncedSearch, page]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const count = (status) => summary?.[status] ?? 0;
  const queueCount = count("SUBMITTED") + count("UNDER_REVIEW");

  const chips = [
    { value: "queue", label: `Review queue (${queueCount})` },
    { value: "NEEDS_CLARIFICATION", label: `Needs clarification (${count("NEEDS_CLARIFICATION")})` },
    { value: "ACTIVATED", label: `Activated (${count("ACTIVATED")})` },
    { value: "REJECTED", label: `Rejected (${count("REJECTED")})` },
    { value: "SUSPENDED", label: `Suspended (${count("SUSPENDED")})` },
    { value: "all", label: "All" },
  ];

  const kpis = summary
    ? [
        { label: "Submitted", value: count("SUBMITTED"), alarm: count("SUBMITTED") > 0 },
        { label: "Under review", value: count("UNDER_REVIEW") },
        { label: "Needs clarification", value: count("NEEDS_CLARIFICATION") },
        { label: "Activated", value: count("ACTIVATED") },
      ]
    : [];

  const totalPages = Math.max(Math.ceil((data.total || 0) / (data.limit || PAGE_SIZE)), 1);
  const searching = Boolean(debouncedSearch);

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Payout Review"
        lede="Clinic payout accounts submitted from the b2b app. Review the details, create the linked account in the Razorpay Dashboard, then activate it here so patient payments are routed to the clinic."
        kpis={kpis}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <FilterChips options={chips} value={filter} onChange={setFilter} />
        <SearchBox
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search clinic, city, state, legal name"
        />
      </Box>

      <DashTable
        columns={[
          { label: "Submitted" },
          { label: "Clinic" },
          { label: "Location" },
          { label: "Legal business name" },
          { label: "Status" },
          { label: "Waiting" },
          { label: "Reviewed by" },
        ]}
        rows={(data.results || []).map((row) => ({
          id: row.clinicId,
          cells: [
            shortDate(row.submittedAt),
            <Box component="span" sx={{ fontWeight: 500 }}>
              {row.clinicName || "—"}
            </Box>,
            [row.city, row.state].filter(Boolean).join(", ") || "—",
            row.legalBusinessName || "—",
            <StatusTag status={row.activationStatus} />,
            <WaitingTag row={row} />,
            row.reviewedByName || "—",
          ],
        }))}
        onRowClick={(id) => setSelectedId(id)}
        emptyText={
          searching
            ? `No payout accounts match "${debouncedSearch}" in this view. Try the All chip.`
            : EMPTY_TEXT[filter] || "No payout accounts match this view."
        }
        errorText={loadFailed ? "Could not load payout accounts." : null}
        onRetry={fetchRows}
        foot={`${(data.results || []).length} shown · ${data.total || 0} total`}
      />

      {data.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "10px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            Page {data.page || page} of {totalPages}
          </span>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Box
              component="button"
              type="button"
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page <= 1}
              sx={btnSx("ghost", page <= 1)}
            >
              Previous
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={page >= totalPages}
              sx={btnSx("ghost", page >= totalPages)}
            >
              Next
            </Box>
          </Box>
        </Box>
      )}

      <Dialog
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreen}
      >
        {selectedId && (
          <PayoutDetail
            clinicId={selectedId}
            canAct={canAct}
            token={token}
            onClose={() => setSelectedId(null)}
            onChanged={fetchRows}
          />
        )}
      </Dialog>

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default PayoutReview;
